import { Navigate, Route, Routes } from 'react-router-dom';
import { ErrorBoundary } from '@/components/error-boundary';
import { Shell } from '@/components/layout/shell';
import { AuthCallbackPage } from '@/features/auth/callback-page';
import { LoginPage } from '@/features/auth/login-page';
import { RequireAuth } from '@/features/auth/require-auth';
import { ProjectsPage } from '@/features/projects/projects-page';
import { QueuePage } from '@/features/queue/queue-page';
import { ReviewPage } from '@/features/review/review-page';

/* ---------------------------------------------------------------------------
   THE ROUTER.

   Four screens. An expert does one job — work the queue — and every route here
   exists to serve it:

     /login            sign in with the Google account an operator registered
     /auth/callback    exchange the refresh cookie for an access token
     /queue            the queue: every frozen project waiting on a reviewer
     /projects         the repo inventory a multi-repo diff needs
     /chats/:chatId    the review itself — thread, diff, promotion

   THE CHAT ID IS IN THE URL, unlike the client dashboard where one chat per
   project makes the project the address. Here a reviewer moves between chats
   across several projects and pastes chat ids into tickets, so the id is the
   address and the review page is deep-linkable — including its tab and its diff
   target, which are query parameters for exactly that reason.

   `RequireAuth` wraps the shell rather than each page, so the session is
   restored once and the sections render inside the chassis. The boundary sits
   outside the routes so a render fault keeps the console rather than
   white-screening it.
--------------------------------------------------------------------------- */

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        <Route
          element={
            <RequireAuth>
              <Shell />
            </RequireAuth>
          }
        >
          <Route path="/queue" element={<QueuePage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/chats/:chatId" element={<ReviewPage />} />
        </Route>

        {/* The queue is the console's home: an expert who lands anywhere else
            has landed on a stale link. */}
        <Route path="/" element={<Navigate to="/queue" replace />} />
        <Route path="*" element={<Navigate to="/queue" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
